import { useEffect, useRef, useState } from 'react';
import { Button } from './Button.js';

export interface CopyButtonProps {
  /** The text to place on the clipboard. */
  value: string;
  /** Visible label, e.g. "Copy reference"; the outcome is reported inline, not in a toast. */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Copies a value and says so in words (04-data §1.14's rule for silent controls). Absent where the
 * browser lacks the clipboard API rather than offered as a control that fails (app-baseline 05 §5.5);
 * the value itself is always rendered as selectable text beside it.
 */
export function CopyButton({ value, label = 'Copy', size = 'sm', className }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);
  if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') return null;

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState('idle'), 2500);
  };

  return (
    <span className={['copy-button', className].filter(Boolean).join(' ')}>
      <Button size={size} variant="ghost" icon={state === 'copied' ? 'check' : 'copy'} onClick={() => void copy()}>
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed — select it instead' : label}
      </Button>
      <span className="visually-hidden" role="status" aria-live="polite">
        {state === 'copied' ? 'Copied to the clipboard' : state === 'failed' ? 'Copying failed; select the text instead' : ''}
      </span>
    </span>
  );
}
